import type { AdminStats } from '@hemmafixa/shared';
import {
  ActivityList,
  Avatar,
  Button,
  ChartCard,
  ChartLegend,
  ContextPanel,
  DonutChart,
  HighlightCard,
  LineChart,
  PageHeader,
  ProfileCard,
  StatStrip,
} from '@hemmafixa/ui';
import {
  ArrowRight,
  BriefcaseBusiness,
  ClipboardCheck,
  Mail,
  Phone,
  ShieldCheck,
  Sticker,
  StickyNote,
} from 'lucide-react';
import { Link } from 'react-router';
import { useCurrentUser } from '@/features/auth/api';
import { useI18n } from '@/i18n/context';
import { linkTo } from '../shared/link-to';
import { OverviewTodos } from '../shared/overview-parts';
import { countOf, useMonthPoints } from '../shared/stats-parts';
import { useAdminCharts, useAdminOverview, useAuditLog } from './api';
import { auditTarget, useAuditAction } from './audit-format';

/**
 * What is waiting for admin, the platform in numbers and charts, and the latest admin actions (P5-07, UI-06).
 * The context panel holds the profile card, the latest admin actions and the review queue.
 */
export function AdminOverviewPage() {
  const { t } = useI18n();
  const user = useCurrentUser();
  const overview = useAdminOverview();
  const charts = useAdminCharts();
  const numbers = overview.data;
  const loading = overview.isPending;
  const o = t.admin.overview;

  return (
    <div className="grid gap-4">
      <PageHeader title={o.title} description={o.description} />
      <StatStrip
        loading={loading}
        items={[
          {
            icon: StickyNote,
            tone: 'brand' as const,
            label: o.jobsToReview,
            value: numbers?.jobsToReview ?? 0,
            link: linkTo('/admin/review'),
          },
          {
            icon: ShieldCheck,
            tone: 'info' as const,
            label: o.providersToVerify,
            value: numbers?.providersToVerify ?? 0,
            link: linkTo('/admin/providers'),
          },
          {
            icon: Sticker,
            tone: 'danger' as const,
            label: o.openReports,
            value: numbers?.openReports ?? 0,
            link: linkTo('/admin/reports'),
          },
          {
            icon: BriefcaseBusiness,
            tone: 'success' as const,
            label: o.openJobs,
            value: numbers?.openJobs ?? 0,
          },
        ]}
      />
      <PlatformCharts stats={charts.data} loading={charts.isPending} />
      <OverviewTodos todos={numbers?.todo ?? []} area="admin" loading={loading} />
      <ContextPanel>
        <ProfileCard
          avatar={<Avatar seed={user.id} name={user.name} size="xl" />}
          name={user.name}
          subtitle={t.areas.admin}
          details={[
            { icon: Mail, label: t.settings.email, value: user.email },
            ...(user.phone ? [{ icon: Phone, label: t.settings.phone, value: user.phone }] : []),
          ]}
        />
        <LatestActions />
        {(numbers?.jobsToReview ?? 0) > 0 && (
          <HighlightCard
            icon={ClipboardCheck}
            title={t.dashboard.reviewTitle}
            action={
              <Button asChild size="sm">
                <Link to="/admin/review">{t.dashboard.openQueue}</Link>
              </Button>
            }
          >
            {t.dashboard.reviewText}
          </HighlightCard>
        )}
      </ContextPanel>
    </div>
  );
}

function PlatformCharts({ stats, loading }: { stats?: AdminStats; loading: boolean }) {
  const { t } = useI18n();
  const months = useMonthPoints();
  const c = t.charts;
  const statuses = t.status.provider;
  const rows = stats?.providerStatuses ?? [];
  const jobs = months(stats?.jobsPerMonth ?? []);
  const series = [
    { name: c.newJobs, color: 1 as const, values: jobs.map((point) => point.value) },
    { name: c.newProposals, color: 2 as const, values: (stats?.proposalsPerMonth ?? []).map((p) => p.value) },
    { name: c.newUsers, color: 3 as const, values: (stats?.usersPerMonth ?? []).map((p) => p.value) },
  ];

  return (
    <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <ChartCard
        title={c.platformActivity}
        description={c.lastSixMonths}
        loading={loading}
        aside={<ChartLegend series={series} className="hidden justify-end pt-1 sm:flex" />}
      >
        <LineChart title={c.platformActivity} labels={jobs} series={series} height={200} legend={false} />
      </ChartCard>
      <ChartCard
        title={c.providersByStatus}
        loading={loading}
        aside={
          <Button asChild variant="more" size="sm">
            <Link to="/admin/providers">
              {t.common.viewAll} <ArrowRight />
            </Link>
          </Button>
        }
      >
        <DonutChart
          title={c.providersByStatus}
          centerValue={rows.reduce((sum, row) => sum + row.count, 0)}
          centerLabel={c.companies}
          segments={[
            { label: statuses.pending, value: countOf(rows, ['pending']), color: 1 },
            { label: statuses.onboarding, value: countOf(rows, ['onboarding']), color: 2 },
            { label: statuses.verified, value: countOf(rows, ['verified']), color: 3 },
            { label: statuses.rejected, value: countOf(rows, ['rejected']), color: 'muted' },
          ]}
        />
      </ChartCard>
    </div>
  );
}

function LatestActions() {
  const { t, relative } = useI18n();
  const query = useAuditLog({ pageSize: 3 });
  const action = useAuditAction();
  const items = (query.data?.items ?? []).map((entry) => {
    const target = auditTarget(entry);
    return {
      id: entry.id,
      time: relative(entry.createdAt),
      title: action(entry.action),
      detail: entry.actor?.name ?? t.admin.audit.system,
      link: target ? linkTo(target) : undefined,
    };
  });
  return (
    <ActivityList
      compact
      className="rounded-none border-0 border-t border-border bg-transparent pt-2 shadow-none [&>div:first-child]:px-1"
      title={t.admin.overview.latestActions}
      items={items}
      loading={query.isPending}
      empty={t.overview.noActivity}
      action={
        <Button asChild variant="more" size="sm">
          <Link to="/admin/audit">
            {t.common.viewAll} <ArrowRight />
          </Link>
        </Button>
      }
    />
  );
}
